import { useEffect, useMemo, useRef, useState } from 'react';
import { ChevronDown, Search, X } from 'lucide-react';

export type MultiSelectOption = {
  id: string;
  label: string;
  sublabel?: string;
};

type Props = {
  options: MultiSelectOption[];
  selectedIds: Set<string>;
  onChange: (next: Set<string>) => void;
  placeholder?: string;       // text shown in the trigger when nothing is picked
  emptyOptionsMessage?: React.ReactNode; // shown when options is empty
  searchPlaceholder?: string;
};

/**
 * Searchable multi-select with pill chips below the trigger.
 *
 * Designed for cases where the option count can grow long (org with 50
 * services, 30 locations, etc.) so a checkbox grid wouldn't fit. Mirrors
 * the affordances admins are used to: type-to-filter, click-to-toggle,
 * removable pills, "Select all" / "Clear" shortcuts when there are
 * options.
 *
 * Self-contained — no external dropdown lib. Closes on outside click or
 * Escape.
 */
export default function MultiSelect({
  options,
  selectedIds,
  onChange,
  placeholder = 'Choose…',
  emptyOptionsMessage,
  searchPlaceholder = 'Search…',
}: Props) {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');
  const rootRef = useRef<HTMLDivElement>(null);
  const searchRef = useRef<HTMLInputElement>(null);

  // Close on outside click or Escape.
  useEffect(() => {
    if (!open) return;
    function onDocPointer(e: MouseEvent) {
      if (!rootRef.current) return;
      if (rootRef.current.contains(e.target as Node)) return;
      setOpen(false);
    }
    function onKey(e: KeyboardEvent) {
      if (e.key === 'Escape') setOpen(false);
    }
    document.addEventListener('mousedown', onDocPointer);
    document.addEventListener('keydown', onKey);
    // Focus the search box for fast filtering.
    queueMicrotask(() => searchRef.current?.focus());
    return () => {
      document.removeEventListener('mousedown', onDocPointer);
      document.removeEventListener('keydown', onKey);
    };
  }, [open]);

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return options;
    return options.filter(
      (o) =>
        o.label.toLowerCase().includes(q) ||
        (o.sublabel?.toLowerCase().includes(q) ?? false),
    );
  }, [options, query]);

  function toggle(id: string) {
    const next = new Set(selectedIds);
    if (next.has(id)) next.delete(id);
    else next.add(id);
    onChange(next);
  }

  function removePill(id: string) {
    const next = new Set(selectedIds);
    next.delete(id);
    onChange(next);
  }

  function selectAll() {
    onChange(new Set(filtered.map((o) => o.id)));
  }

  function clearAll() {
    onChange(new Set());
  }

  const selected = options.filter((o) => selectedIds.has(o.id));

  if (options.length === 0 && emptyOptionsMessage) {
    return <div className="ms-empty">{emptyOptionsMessage}</div>;
  }

  return (
    <div className="multi-select" ref={rootRef}>
      <button
        type="button"
        className="ms-trigger"
        onClick={() => setOpen((v) => !v)}
        aria-haspopup="listbox"
        aria-expanded={open}
      >
        <span className="ms-trigger-label">
          {selected.length === 0
            ? placeholder
            : `${selected.length} selected`}
        </span>
        <ChevronDown size={16} />
      </button>

      {selected.length > 0 && (
        <div className="ms-pills">
          {selected.map((o) => (
            <span key={o.id} className="ms-pill">
              <span>{o.label}</span>
              <button
                type="button"
                className="ms-pill-x"
                onClick={() => removePill(o.id)}
                aria-label={`Remove ${o.label}`}
              >
                <X size={12} />
              </button>
            </span>
          ))}
        </div>
      )}

      {open && (
        <div className="ms-popover" role="listbox">
          <div className="ms-search">
            <Search size={14} />
            <input
              ref={searchRef}
              type="text"
              placeholder={searchPlaceholder}
              value={query}
              onChange={(e) => setQuery(e.target.value)}
            />
          </div>

          <div className="ms-actions">
            <button type="button" className="ghost small" onClick={selectAll}>
              Select all {query ? '(filtered)' : ''}
            </button>
            <span className="spacer" />
            <button type="button" className="ghost small" onClick={clearAll}>
              Clear
            </button>
          </div>

          <div className="ms-list">
            {filtered.length === 0 ? (
              <div className="ms-list-empty muted small">No matches.</div>
            ) : (
              filtered.map((o) => {
                const checked = selectedIds.has(o.id);
                return (
                  <label key={o.id} className="ms-option">
                    <input
                      type="checkbox"
                      checked={checked}
                      onChange={() => toggle(o.id)}
                    />
                    <span className="ms-option-label">
                      <strong>{o.label}</strong>
                      {o.sublabel && (
                        <span className="muted small"> · {o.sublabel}</span>
                      )}
                    </span>
                  </label>
                );
              })
            )}
          </div>
        </div>
      )}
    </div>
  );
}
